/**
 * Універсальний компонент індикатора завантаження
 * Підтримує різні розміри, кольори та типи анімації
 * @component LoadingSpinner
 * @file LoadingSpinner.tsx
 * @path frontend/src/components/common/LoadingSpinner/LoadingSpinner.tsx
 */

import React from 'react';
import './style.css';

// Типи розмірів спінера
export type SpinnerSize = 'small' | 'medium' | 'large' | 'xlarge';

// Типи варіантів спінера
export type SpinnerVariant = 'primary' | 'secondary' | 'white' | 'success' | 'warning' | 'error';

// Пропси компонента
interface LoadingSpinnerProps {
  /**
   * Розмір спінера
   * @default 'medium'
   */
  size?: SpinnerSize;
  
  /**
   * Варіант кольору спінера
   * @default 'primary'
   */
  variant?: SpinnerVariant;
  
  /**
   * Текст, що відображається під спінером (опційно)
   */
  text?: string;
  
  /**
   * Чи повинен спінер займати всю доступну висоту
   * @default false
   */
  fullHeight?: boolean;
  
  /**
   * Додаткові CSS класи
   */
  className?: string;
  
  /**
   * Інлайн стилі
   */
  style?: React.CSSProperties;
}

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'medium',
  variant = 'primary',
  text,
  fullHeight = false,
  className = '',
  style
}) => {
  /**
   * Отримує CSS класи на основі пропсів
   */
  const getSpinnerClasses = (): string => {
    const classes = [
      'loading-spinner',
      `loading-spinner--${size}`,
      `loading-spinner--${variant}`,
      className
    ];
    
    if (fullHeight) {
      classes.push('loading-spinner--full-height');
    }
    
    return classes.filter(Boolean).join(' ');
  };

  /**
   * Отримує розмір спінера в пікселях
   */
  const getSpinnerSize = (): number => {
    const sizeMap: Record<SpinnerSize, number> = {
      small: 20,
      medium: 32,
      large: 48,
      xlarge: 64
    };
    
    return sizeMap[size];
  };

  return (
    <div 
      className={getSpinnerClasses()}
      style={style}
      role="status"
      aria-live="polite"
      aria-label={text || 'Завантаження...'}
    >
      <div className="loading-spinner__container">
        {/* Основний спінер */}
        <svg
          className="loading-spinner__svg"
          width={getSpinnerSize()}
          height={getSpinnerSize()}
          viewBox="0 0 50 50"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Коло фону */}
          <circle
            className="loading-spinner__circle-bg"
            cx="25"
            cy="25"
            r="20"
            fill="none"
            strokeWidth="4"
          />
          
          {/* Анімоване коло */}
          <circle
            className="loading-spinner__circle-fill"
            cx="25"
            cy="25"
            r="20"
            fill="none"
            strokeWidth="4"
            strokeLinecap="round"
          />
        </svg>
        
        {/* Текст (якщо вказано) */}
        {text && (
          <span className="loading-spinner__text">
            {text}
          </span>
        )}
      </div>
    </div>
  );
};

export default LoadingSpinner;